import { useCallback, useEffect, useRef, useState } from 'react';
import { Chip } from '../../components/shared';
import { searchUsers, type UserSummary } from '../../api/users';
import { listMailAddresses } from '../../api/mail';
import { useModalLeave } from '../../hooks/useModalLeave';
import { tMail } from '../../lib/mail-i18n';

export interface PickedUser {
  id: string;
  name: string;
  email: string;
  position?: string | null;
  /**
   * 사내 사용자가 아니라 **밖의 주소** (migration 113).
   *
   * 이때 `id` 는 `ext:<주소>` 다 — 우리 사용자 id 와 절대 섞이지 않게 하려는 것이다.
   * 숫자 id 로 두면 어딘가에서 사용자 id 로 오해돼 엉뚱한 사람에게 간다.
   */
  external?: boolean;
}

/** 밖의 주소를 칩으로 만들 때 쓰는 id — 사용자 id 와 섞이지 않도록 접두를 붙인다. */
export const EXTERNAL_ID_PREFIX = 'ext:';

/** 이메일 주소로 보이는가. 서버(`isEmailAddress`)와 같은 잣대다 — 모양만 본다. */
export function looksLikeEmail(value: string): boolean {
  const v = value.trim();
  if (v.length === 0 || v.length > 320) return false;
  return /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]{2,}$/.test(v);
}

/** 주소 하나를 칩으로. 이름 자리에는 주소를 그대로 둔다(빈 칸보다 낫다). */
export function externalChip(address: string): PickedUser {
  const v = address.trim();
  return { id: `${EXTERNAL_ID_PREFIX}${v.toLowerCase()}`, name: v, email: v, external: true };
}

interface RecipientPickerProps {
  label: string;
  /**
   * 목록 id 의 씨앗 (2026-08-23) — 라벨과 **따로** 둔다.
   * 라벨이 번역되면서 id 까지 언어마다 달라지는 것을 막는다(`recipient-list-받는사람`).
   * 안 주면 지금까지처럼 라벨에서 뽑는다(무회귀).
   */
  fieldKey?: string;
  value: PickedUser[];
  onChange: (next: PickedUser[]) => void;
  /** ComposeModal 이 to/cc/bcc 별로 같은 user 를 중복으로 들어가지 않도록 검사 (선택). */
  excludeIds?: string[];
  /** 첫 mount 시 input 에 자동 focus (focus trap 의 컨테이너 안 첫 focusable 명시용). */
  autoFocus?: boolean;
  /**
   * 아직 수신자로 확정되지 않은 입력을 부모에게 알린다(2026-08-20).
   *
   * 이 칸은 **목록에서 골라야** 수신자가 된다. 그런데 그 사실이 화면 어디에도 없어서,
   * 주소를 다 적고 보낸 메일이 받는사람 0명으로 나간 적이 있다(mail_messages id=29 —
   * TO 0 · CC 1 · BCC 1). 부모(ComposeModal)가 이 값을 받아 경고를 띄운다.
   */
  onPendingChange?: (pending: string) => void;
}

/** 형식만 본다 — 실제로 보낼 수 있는지는 서버가 판단한다. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 자동완성 한 줄 (2026-08-23).
 *
 * 두 곳에서 온다 — **사내 명부**와 **내가 주고받은 주소**. 화면에서는 같은 모양으로 서되,
 * 어디서 왔는지는 `external` 이 말한다(밖의 주소는 칩 색이 다르고 id 에 접두가 붙는다).
 */
interface Suggestion extends PickedUser {
  /** 주고받은 주소일 때, 몇 번 오갔나 — 목록에 "3번" 처럼 적는다. */
  uses?: number;
}

/**
 * RecipientPicker (P3-MAIL-04) — 메일 수신자 chip + 자동완성 picker.
 *
 * 동작:
 *   - input 에 query 입력 → 200ms debounce 후 GET /users?search= 호출.
 *   - 결과 dropdown 에 표시 (최대 8명).
 *   - ↑/↓ 화살표로 highlight, Enter 로 선택, Esc 로 닫기.
 *   - chip 표시 + X 클릭으로 제거.
 *   - 사용자가 직접 BigInt id 를 입력할 수 있도록 backspace fallback (chip 마지막 제거).
 *
 * a11y:
 *   - role="combobox" + aria-expanded + aria-activedescendant.
 *   - chip 별 aria-label.
 */
export function RecipientPicker({ label, fieldKey, value, onChange, excludeIds = [], autoFocus = false, onPendingChange }: RecipientPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Suggestion[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = `recipient-list-${(fieldKey ?? label).toLowerCase()}`;
  // unmount transition — open + 결과 있는 동안 popover 표시. close 시 150ms
  // wc-popover-leave 진행 후 DOM 제거. results 를 latched 로 보존해 leaving 중
  // 빈 list flash 방지 (addUser 시 setResults([]) 와 동시에 setOpen(false) 호출).
  const popoverInput = open && results.length > 0 ? results : null;
  const {
    shouldRender: popoverRender,
    isLeaving: popoverLeaving,
    latched: latchedResults,
  } = useModalLeave(popoverInput, 150);

  // debounce search — race 봉합 다중 보호:
  //   1. cancelled flag (outer effect scope) — stale promise resolve 차단.
  //   2. setTimeout body 진입 즉시 cancelled 가드 (F3) — unmount-after-fire race 시
  //      setLoading(true) 가 unmounted 호출되는 것 차단.
  //   3. AbortController (F4) — inflight 네트워크 즉시 abort (backend DB scan 비용 차단).
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    const abortController = new AbortController();
    const handle = setTimeout(() => {
      if (cancelled) return; // F3 — fire 직후 cleanup 이 이미 cancelled set 한 race.
      setLoading(true);
      setError(null);
      /*
       * 두 곳을 **함께** 뒤진다 (2026-08-23).
       *   · 사내 명부      — 아직 한 번도 안 주고받은 동료도 찾아야 한다.
       *   · 주고받은 주소  — 밖의 거래처는 명부에 없다. 여태 이 길이 없어서, 한 번 보냈던
       *                     곳도 다시 칠 때 아무 도움을 못 받았다.
       * ★ 한쪽이 실패해도 다른 쪽은 보여 준다 — 자동완성이 통째로 죽는 것보다 낫다.
       * ★ 사내 명부를 위에 둔다. 같은 주소가 양쪽에 있으면 사내 것 하나만 남긴다 —
       *   사내 사용자는 id 로 보내야 서버가 사내 메일로 처리한다(주소로 보내면 밖으로 나간다).
       */
      Promise.allSettled([
        searchUsers(query.trim(), 8, abortController.signal),
        listMailAddresses(query.trim(), 8, abortController.signal),
      ])
        .then(([dir, book]) => {
          if (cancelled) return;
          const users: UserSummary[] = dir.status === 'fulfilled' ? dir.value : [];
          const addrs = book.status === 'fulfilled' ? book.value : [];
          // exclude 이미 선택된 + excludeIds (다른 type 수신자) 필터.
          const excluded = new Set([...value.map((v) => v.id), ...excludeIds]);
          const seenEmail = new Set<string>();

          const fromDir: Suggestion[] = users
            .filter((u) => !excluded.has(u.id))
            .map((u) => {
              seenEmail.add(u.email.toLowerCase());
              return { id: u.id, name: u.name, email: u.email, position: u.position };
            });

          const fromBook: Suggestion[] = addrs
            .filter((a) => !seenEmail.has(a.email.toLowerCase()))
            .map((a) => ({ ...externalChip(a.email), name: a.name || a.email, uses: a.uses }))
            .filter((sug) => !excluded.has(sug.id));

          setResults([...fromDir, ...fromBook].slice(0, 8));
          setHighlight(0);
          setOpen(true);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // axios CanceledError — abort 결과는 silent.
          if (err instanceof Error && err.name === 'CanceledError') return;
          setError(err instanceof Error ? err.message : 'search_failed');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
      abortController.abort();
    };
  }, [query, value, excludeIds]);

  // 확정되지 않은 입력을 부모에게 알린다. 칩이 되면 query 가 비므로 자동으로 해제된다.
  useEffect(() => {
    onPendingChange?.(query.trim());
  }, [query, onPendingChange]);

  // unmount 시에도 남은 경고를 걷어 준다 — 모달을 닫았다 다시 열면 옛 경고가 붙어 있으면 안 된다.
  useEffect(() => () => { onPendingChange?.(''); }, [onPendingChange]);

  /**
   * 고른 한 줄을 칩으로.
   * ★ `external` 을 그대로 넘긴다 — 주고받은 주소는 사내 사용자가 아니므로 칩이 그렇게
   *   서야 서버가 SMTP 로 내보낸다(사내 id 로 담으면 "그런 수신자 없음" 으로 거절된다).
   */
  const addUser = useCallback((u: Suggestion) => {
    onChange([...value, {
      id: u.id, name: u.name, email: u.email, position: u.position, ...(u.external ? { external: true } : {}),
    }]);
    setQuery('');
    setResults([]);
    setOpen(false);
    setHighlight(0);
    inputRef.current?.focus();
  }, [onChange, value]);

  /**
   * 검색에 안 걸리는 **밖의 주소**를 그대로 칩으로 (migration 113).
   *
   * 여태 여기서 Enter 를 치면 검색 결과가 없을 때 **아무 일도 일어나지 않았다** — 그래서
   * 외부 주소는 받는 사람이 될 수 없었고, 받아온 메일에 답장도 못 했다.
   * 이미 담긴 주소면 조용히 무시한다(같은 주소로 두 통이 가지 않게).
   */
  const addExternal = useCallback((raw: string): boolean => {
    if (!looksLikeEmail(raw)) return false;
    const chip = externalChip(raw);
    if (!value.some((v) => v.id === chip.id)) onChange([...value, chip]);
    setQuery('');
    setResults([]);
    setOpen(false);
    setHighlight(0);
    inputRef.current?.focus();
    return true;
  }, [onChange, value]);

  const removeUser = useCallback((id: string) => {
    onChange(value.filter((v) => v.id !== id));
    inputRef.current?.focus();
  }, [onChange, value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !query && value.length > 0) {
      e.preventDefault();
      const last = value[value.length - 1]!;
      removeUser(last.id);
      return;
    }
    // ★ 아래 가드보다 **먼저** 본다 — 검색 결과가 없을 때가 바로 외부 주소인 경우다.
    //   (여태 이 가드 때문에 결과 없는 Enter 가 통째로 무시됐다.)
    if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && query.trim().length > 0) {
      const noHit = !open || results.length === 0 || !results[highlight];
      if (noHit && addExternal(query)) {
        e.preventDefault();
        return;
      }
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[highlight];
      if (target) addUser(target);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [addExternal, addUser, highlight, open, query, removeUser, results, value]);

  return (
    <div className="recipient-picker">
      <span className="mail-compose-field-label">{label}</span>
      <div className="recipient-picker-input-wrap">
        <div className="recipient-picker-chips" role="list" aria-label={`${label} ${tMail('mail.recipient.aria_selected_suffix')}`}>
          {value.map((v) => (
            /*
              정본 D14 — 칩과 `<input>` 이 한 줄에 섞여 흐르므로 `inline`(= `<span role="listitem">`).
              ★ 바깥 주소는 `quiet` 다 — 사내와 **눈으로 갈려야** 한다(글자로도 「외부」라고 말한다).
            */
            <Chip
              key={v.id}
              inline
              size="md"
              variant={v.external ? 'quiet' : 'primary'}
              title={`${v.name} <${v.email}>`}
              onRemove={() => removeUser(v.id)}
              removeLabel={`${v.name} ${tMail('mail.recipient.aria_remove_suffix')}`}
            >
              {v.name}
            </Chip>
          ))}
          <input
            ref={inputRef}
            className="recipient-picker-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (results.length > 0) setOpen(true); }}
            onBlur={() => setTimeout(() => {
              setOpen(false);
              // 주소를 적어 놓고 Enter 없이 다른 칸으로 넘어가는 일이 잦다 — 그때도 확정한다.
              // (확정 안 된 입력은 "받는사람 0명" 경고로만 남아 왜 안 가는지 알기 어렵다.)
              if (looksLikeEmail(query)) addExternal(query);
            }, 150)}  // dropdown 클릭 허용 위해 지연
            placeholder={value.length === 0 ? tMail('mail.recipient.placeholder_search') : ''}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-label={label}
            autoFocus={autoFocus}
          />
        </div>
        {popoverRender && latchedResults && (
          <ul
            id={listId}
            className={`recipient-picker-results${popoverLeaving ? ' recipient-picker-results--leaving' : ''}`}
            role="listbox"
            aria-hidden={popoverLeaving || undefined}
          >
            {latchedResults.map((u, i) => (
              <li
                key={u.id}
                className={`recipient-picker-result${i === highlight ? ' recipient-picker-result--highlight' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); addUser(u); }}
                onMouseEnter={() => setHighlight(i)}
                role="option"
                aria-selected={i === highlight}
              >
                <div className="recipient-picker-result-name">
                  {u.name}
                  {/* 밖의 주소임을 밝힌다 — 사내 동료와 한 목록에 섞이므로. */}
                  {u.external && (
                    <span className="recipient-picker-result-tag">
                      {tMail('mail.sender.external_badge')}
                    </span>
                  )}
                </div>
                <div className="recipient-picker-result-meta">
                  {u.email}
                  {u.position ? ` · ${u.position}` : ''}
                  {/* 몇 번 주고받았나 — 왜 위에 떠 있는지가 그 자리에 적혀 있어야 한다. */}
                  {u.uses ? ` · ${tMail('mail.recipient.uses').replace('{n}', String(u.uses))}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
        {loading && <div className="recipient-picker-loading">{tMail('mail.recipient.searching')}</div>}
        {error && <div className="recipient-picker-error">{error}</div>}
        {/* 찾는 중도 아니고 결과도 없는데 글자가 남아 있다 = 아직 수신자가 아니다.
            메일 주소 모양이면 이유가 다르므로(사내 검색에 안 걸린다) 다른 문구를 준다. */}
        {!loading && !error && query.trim().length > 0 && results.length === 0 && (
          <div className="recipient-picker-hint" role="status">
            {EMAIL_SHAPE.test(query.trim())
              ? tMail('mail.recipient.pending_external')
              : tMail('mail.recipient.pending_hint')}
          </div>
        )}
      </div>
    </div>
  );
}
